/**
 * UserFilters Component
 * Filter controls for the user list
 */

import { Search, X, Filter } from 'lucide-react'
import type { UserRole, UserFilters as UserFiltersType } from '@/types/user.types'
import { getRoleDisplayName } from '@/types/auth.types'

// ============================================================================
// TYPES
// ============================================================================

interface UserFiltersProps {
  filters: UserFiltersType
  onFiltersChange: (filters: Partial<UserFiltersType>) => void
  onClearFilters: () => void
  totalCount: number
  filteredCount: number
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ROLE_OPTIONS: Array<{ value: UserRole | 'all'; label: string }> = [
  { value: 'all', label: 'All Roles' },
  { value: 'firm_owner', label: 'CA Firm Owner' },
  { value: 'firm_admin', label: 'Firm Administrator' },
  { value: 'ca_partner', label: 'CA Partner' },
  { value: 'technical_assistant', label: 'Technical Assistant' },
  { value: 'research_assistant', label: 'Research Assistant' },
  { value: 'individual_client', label: 'Individual Client' },
  { value: 'business_owner_client', label: 'Business Owner Client' },
  { value: 'authorized_client', label: 'Authorized Client' },
  { value: 'staff', label: 'Staff (Needs Classification)' },
  { value: 'client', label: 'Client (Needs Classification)' },
]

const STATUS_OPTIONS: Array<{ value: 'all' | 'active' | 'inactive' | 'pending'; label: string }> = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
]

// ============================================================================
// COMPONENT
// ============================================================================

export function UserFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  totalCount,
  filteredCount,
}: UserFiltersProps) {
  const hasActiveFilters =
    filters.searchQuery ||
    (filters.role && filters.role !== 'all') ||
    (filters.status && filters.status !== 'all')

  return (
    <div className="space-y-4">
      {/* Search and Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={filters.searchQuery || ''}
            onChange={(e) => onFiltersChange({ searchQuery: e.target.value })}
            className="input input-bordered w-full pl-10 pr-10"
          />
          {filters.searchQuery && (
            <button
              type="button"
              onClick={() => onFiltersChange({ searchQuery: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Role Filter */}
        <select
          value={filters.role || 'all'}
          onChange={(e) =>
            onFiltersChange({ role: e.target.value as UserRole | 'all' | undefined })
          }
          className="select select-bordered w-full sm:w-40"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={filters.status || 'all'}
          onChange={(e) =>
            onFiltersChange({
              status: e.target.value as 'all' | 'active' | 'inactive' | 'pending' | undefined,
            })
          }
          className="select select-bordered w-full sm:w-36"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Active Filters & Results Count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Results count */}
          <span className="text-sm text-text-secondary">
            {filteredCount === totalCount ? (
              <>
                Showing all <strong>{totalCount}</strong> users
              </>
            ) : (
              <>
                Showing <strong>{filteredCount}</strong> of <strong>{totalCount}</strong> users
              </>
            )}
          </span>

          {/* Active filter badges */}
          {hasActiveFilters && (
            <>
              <span className="text-text-tertiary">•</span>
              <div className="flex items-center gap-2 flex-wrap">
                {filters.searchQuery && (
                  <span className="badge badge-outline badge-sm gap-1">
                    Search: "{filters.searchQuery}"
                    <button
                      type="button"
                      onClick={() => onFiltersChange({ searchQuery: '' })}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {filters.role && filters.role !== 'all' && (
                  <span className="badge badge-outline badge-sm gap-1">
                    Role: {getRoleDisplayName(filters.role)}
                    <button
                      type="button"
                      onClick={() => onFiltersChange({ role: 'all' })}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {filters.status && filters.status !== 'all' && (
                  <span className="badge badge-outline badge-sm gap-1">
                    Status: {filters.status}
                    <button
                      type="button"
                      onClick={() => onFiltersChange({ status: 'all' })}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Clear all filters */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="btn btn-ghost btn-sm gap-1 text-text-secondary"
          >
            <Filter className="w-4 h-4" />
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}

export default UserFilters
